/**
 * Utility functions for hotel filtering and search matching across Alora Rejser.
 */

// Explicit list of apartment / aparthotel IDs
export const APARTMENT_HOTEL_IDS = [
  'hipotels-bahia-grande',
  'aparthotel-flora',
  'appartementhaus-renate',
  'quartier-libre-marais',
  'protur-safari-park',
  'zafiro-mallorca'
];

// Explicit list of Adults Only hotel IDs
export const ADULTS_ONLY_HOTEL_IDS = [
  'forum-boutique-hotel-spa',
  'hoposa-daina'
];

/**
 * Checks if a hotel has a pool lift.
 * Canonical source of truth: accessibilityIcons containing 'pool_lift'.
 */
export function hasPoolLift(hotel) {
  if (!hotel) return false;

  // Forum explicitly has neither pool lift nor ramp (even though it mentions Puerto Azul's pool lift in description)
  // Ca Na Nina has pool lift "på vej", not ready yet
  // Anissa Beach & Village expects pool lift in season 2027
  if (hotel.id === 'forum-boutique-hotel-spa' || hotel.id === 'ca-na-nina' || hotel.id === 'anissa-beach-village') {
    return false;
  }

  // 1. Check accessibilityIcons (the authoritative attribute)
  if (Array.isArray(hotel.accessibilityIcons) && hotel.accessibilityIcons.includes('pool_lift')) {
    return true;
  }

  // 2. Check features
  if (Array.isArray(hotel.features)) {
    if (hotel.features.some(f => /pool\s*lift|poollift|pool\s*med\s*lift/i.test(f))) {
      return true;
    }
  }

  // 3. Check amenities
  if (Array.isArray(hotel.amenities)) {
    if (hotel.amenities.some(a => /pool\s*lift|poollift|pool\s*med\s*lift/i.test(a))) {
      return true;
    }
  }

  // 4. Description check (safe fallback)
  const desc = (hotel.description || '').toLowerCase();
  if (/pool\s*lift|poollift/i.test(desc) && !desc.includes('hverken') && !desc.includes('på vej')) {
    return true;
  }

  return false;
}

/**
 * Checks if a hotel is Adults Only.
 */
export function isAdultsOnly(hotel) {
  if (!hotel) return false;
  if (ADULTS_ONLY_HOTEL_IDS.includes(hotel.id)) return true;
  if (hotel.features?.some(f => /adults\s*only|voksen/i.test(f))) return true;
  if (/adults\s*only|voksenhotel/i.test(hotel.name || '')) return true;
  if (/adults\s*only/i.test(hotel.description || '')) return true;
  return false;
}

/**
 * Checks if a hotel is child / family friendly.
 * Per customer requirement: in search and filtering, all hotels are child-friendly EXCEPT adults-only hotels.
 */
export function isChildFriendly(hotel) {
  if (!hotel) return false;
  return !isAdultsOnly(hotel);
}

/**
 * Checks if a hotel is an apartment / aparthotel.
 * Hoposa Daina is strictly hotel rooms only.
 */
export function isApartment(hotel) {
  if (!hotel) return false;
  if (hotel.id === 'hoposa-daina') return false;

  if (APARTMENT_HOTEL_IDS.includes(hotel.id)) return true;
  if (hotel.features?.some(f => /lejlighed|lejligheder|apartment|ferielejlighed/i.test(f))) return true;
  if (/aparthotel|appartementhaus|lejlighedshotel/i.test(hotel.name || '')) return true;
  if (hotel.amenities?.some(a => /køkken|lejlighed|kitchen|apartment/i.test(a))) return true;

  return false;
}

/**
 * Checks if a hotel has wellness / spa.
 */
export function isWellness(hotel) {
  if (!hotel) return false;
  if (hotel.features?.some(f => /wellness|spa/i.test(f))) return true;
  if (hotel.amenities?.some(a => /wellness|spa/i.test(a))) return true;
  if (/wellness|spa/i.test(hotel.name || '')) return true;
  if (/\b(wellness|spa)\b/i.test(hotel.description || '')) return true;
  return false;
}

/**
 * General text search matcher for a single term against a hotel.
 */
function matchesGeneralQuery(hotel, query, destTitle = '') {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;

  if ((hotel.name || '').toLowerCase().includes(q)) return true;
  if ((hotel.area || '').toLowerCase().includes(q)) return true;
  if ((destTitle || '').toLowerCase().includes(q)) return true;

  if (Array.isArray(hotel.features) && hotel.features.some(f => f.toLowerCase().includes(q))) {
    return true;
  }
  if (Array.isArray(hotel.amenities) && hotel.amenities.some(a => a.toLowerCase().includes(q))) {
    return true;
  }

  // Guardrails when searching in descriptions:
  // - Never match Hoposa on apartment queries
  if (hotel.id === 'hoposa-daina' && /lejlighed|apartment/i.test(q)) {
    return false;
  }
  // - Never match Forum or Hoposa on child/family queries
  if (isAdultsOnly(hotel) && /børn|børne|familie|child|kid/i.test(q)) {
    return false;
  }
  // - Never match hotels without pool lift on pool lift queries
  if (!hasPoolLift(hotel) && /pool\s*lift|poollift/i.test(q)) {
    return false;
  }

  const desc = (hotel.description || '').toLowerCase();
  const longDesc = (hotel.longDescription || '').toLowerCase();

  return desc.includes(q) || longDesc.includes(q);
}

/**
 * Main hotel search matcher that handles pool lift, apartments, children,
 * adults only, wellness, and general free-text searches including compound queries.
 */
export function matchesHotelSearch(hotel, searchTerm, destTitle = '') {
  if (!searchTerm) return true;
  const raw = searchTerm.trim();
  if (!raw) return true;

  const q = raw.toLowerCase();

  // Pool lift search keywords
  const poolLiftRegex = /\b(pool\s*lift|poollift|pool\s*liften|poolliften|lift\s*til\s*pool|bassinlift|pool\s*med\s*lift|lift)\b/i;
  // Apartment search keywords
  const apartmentRegex = /\b(lejlighed|lejligheder|ferielejlighed|ferielejligheder|apartment|apartments|aparthotel|lejlighedshotel)\b/i;
  // Child/family search keywords
  const childRegex = /\b(børnevenlig|børnevenligt|børnevenlige|børnevenlighed|børnehotel|børnehoteller|børn|børne|familie|familier|familievenlig|familievenligt|familievenlige|familiehotel|familiehoteller|child[\s-]*friendly|family[\s-]*friendly|kids|children)\b/i;
  // Adults only keywords
  const adultsRegex = /\b(adults\s*only|voksen|voksne|voksenhotel|voksenhoteller)\b/i;
  // Wellness keywords
  const wellnessRegex = /\b(wellness|spa|kurbad)\b/i;

  // 1. Compound or single pool lift search
  if (poolLiftRegex.test(q)) {
    if (!hasPoolLift(hotel)) return false;
    const remainder = q.replace(poolLiftRegex, '').trim();
    if (!remainder) return true;
    return matchesGeneralQuery(hotel, remainder, destTitle);
  }

  // 2. Compound or single apartment search
  if (apartmentRegex.test(q)) {
    if (!isApartment(hotel)) return false;
    const remainder = q.replace(apartmentRegex, '').trim();
    if (!remainder) return true;
    return matchesGeneralQuery(hotel, remainder, destTitle);
  }

  // 3. Compound or single child/family search
  if (childRegex.test(q)) {
    if (!isChildFriendly(hotel)) return false;
    const remainder = q.replace(childRegex, '').replace(/\b(hoteller|hotel|hotels|resort)\b/gi, '').trim();
    if (!remainder) return true;
    return matchesGeneralQuery(hotel, remainder, destTitle);
  }

  // 4. Compound or single adults only search
  if (adultsRegex.test(q)) {
    if (!isAdultsOnly(hotel)) return false;
    const remainder = q.replace(adultsRegex, '').trim();
    if (!remainder) return true;
    return matchesGeneralQuery(hotel, remainder, destTitle);
  }

  // 5. Compound or single wellness search
  if (wellnessRegex.test(q)) {
    if (!isWellness(hotel)) return false;
    const remainder = q.replace(wellnessRegex, '').trim();
    if (!remainder) return true;
    return matchesGeneralQuery(hotel, remainder, destTitle);
  }

  // 6. General free-text query
  return matchesGeneralQuery(hotel, q, destTitle);
}
